import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DragDropContext, Droppable, DropResult, Draggable } from 'react-beautiful-dnd'
import { useParams, useHistory } from 'react-router-dom'
import Container from '../../atoms/page/Page'
import InlineGroup from '../../atoms/inlinegroup/InlineGroup'
import LoadingSpinner from '../../atoms/loadingspinner/LoadingSpinner'
import ReportingTemplateDelete from './ReportingTemplateDelete'
import Banner from '../../atoms/banner/Banner'
import IconButton from '../../molecules/iconbutton/IconButton'
import { useReportingTemplate } from '../../../hooks/useReportingTemplate'
import { CloneReportingTemplate } from './actions/CloneReportingTemplate'
import VerticalGroup from '../../atoms/verticalgroup/VerticalGroup'
import { data as fakePreviewData } from '../results/data/fakePreviewData'
import RoundResultsPage from '../results/RoundResultsPage'
import Button from '../../atoms/button/Button'
import useElementSize from '../../../hooks/useElementSize'
import api from '../../../services/api'
import Clickable from '../../atoms/clickable/Clickable'
import ContentStretcher from './components/ContentStretcher'
import ReportingPagePreviewTile, { THUMBNAIL_COLUMN_WIDTH, RENDERED_DIMENSIONS } from './components/ReportingPagePreviewTile'
import { AddReportingTemplateLayout } from './actions/AddReportingTemplateLayout'
import { EditRoundRestrictions } from './actions/EditRoundRestrictions'
import { AutoConfigure } from './actions/Autoconfigure'
import { GenericPreview } from './actions/GenericPreview'
import DeleteReportingTemplateLayout from './ReportingTemplateDeleteLayout'
import Text from '../../atoms/text/Text'

interface AssessmentState {
  deleteModalOpen: boolean
  deleteLayoutModalOpen: boolean
}

const SIDEBAR_WIDTH = 150

function ReportingTemplateDetails() {
  let { reportingTemplateId } = useParams<{ reportingTemplateId: string }>()
  const history = useHistory()
  const [apiError, setApiError] = useState<string | null>(null)
  const [selectedLayout, setSelectedLayout] = useState<API.ReportingTemplateLayoutResponse | null>(null)
  const [apiUpdating, setApiUpdating] = useState(false)
  const [layouts, setLayouts] = useState<API.ReportingTemplateLayoutResponse[]>([])
  const [containerRef, { height: containerHeight, width: containerWidth }] = useElementSize()

  const {previewHeight, previewWidth} = useMemo(() => {
    if (containerHeight) {
      const heightAvailable = containerHeight
      const widthAvailable = containerWidth - THUMBNAIL_COLUMN_WIDTH - SIDEBAR_WIDTH

      const scale = Math.min(
        1,
        Math.min(widthAvailable/RENDERED_DIMENSIONS.width, heightAvailable/RENDERED_DIMENSIONS.height)
      )

      return { previewHeight: scale * RENDERED_DIMENSIONS.height, previewWidth: scale * RENDERED_DIMENSIONS.width}
    }
    return { previewHeight: 0, previewWidth: 0 }
  }, [containerHeight, containerWidth])

  const { inProgress, data, error, refresh } = useReportingTemplate(reportingTemplateId)

  useEffect(() => {
    if (data?.layouts) {
      setLayouts(data.layouts)
      setSelectedLayout(data.layouts[0])
    }
  }, [data?.layouts])

  const [{ deleteModalOpen, deleteLayoutModalOpen }, setUIState] = useState<AssessmentState>({
    deleteModalOpen: false,
    deleteLayoutModalOpen: false
  })

  const onDeleteComplete = useCallback(() => {
    setUIState(prevState => ({ ...prevState, deleteModalOpen: false }))
    history.push('/reporting-templates')
  }, [history])

  const onDeleteLayoutComplete = useCallback(() => {
    setUIState(prevState => ({ ...prevState, deleteLayoutModalOpen: false }))
    refresh()
  }, [refresh])

  const onLayoutUpdate = useCallback(async (layoutId: string, data: API.ReportingTemplateLayoutUpdateRequest) => {
    try {
      setApiUpdating(true)
      setLayouts((list) => {
        const result = Array.from(list)
        const index = result.findIndex(r => r.id === layoutId)
        result[index] = { ...result[index], ...data }
        return result
      })
      await api.updateReportingTemplateLayout(reportingTemplateId, layoutId, data)
      refresh()
      setApiUpdating(false)
    } catch (e) {
      setApiUpdating(false)
      setApiError(e.message)
    }
  }, [reportingTemplateId, refresh])

  const onDragEnd = useCallback(async (result: DropResult) => {
    if (!result.destination) return

    const sourceIndex = result.source.index
    const destIndex = result.destination.index

    let layoutsResult: API.ReportingTemplateLayoutResponse[] = []

    setLayouts((list) => {
      const result = Array.from(list)
      const [removed] = result.splice(sourceIndex, 1)
      result.splice(destIndex, 0, removed)
      layoutsResult = result
      return result
    })

    for (let i = 0; i < layoutsResult.length; i++) {
      const val = layoutsResult[i]
      if (val.config && val.config.type === 'custom_image') {
        delete layoutsResult[i].config.imageUrl
      }
    }

    try {
      setApiUpdating(true)
      await api.patchReportingTemplate(reportingTemplateId, { layouts: layoutsResult })
      refresh()
      setApiUpdating(false)
    } catch (e) {
      setApiUpdating(false)
      setApiError(e.message)
    }
  }, [refresh, reportingTemplateId])

  const onCustomTemplateEdit = useCallback((val: API.ReportingTemplateLayoutUpdateRequest) => {
    if (selectedLayout?.id) {
      if (val.config && val.config.type === 'custom_image') {
        delete val.config.imageUrl
      }
      onLayoutUpdate(selectedLayout.id, val)
    }
  }, [onLayoutUpdate, selectedLayout?.id])

  return (
    <>
      {deleteModalOpen && !!data && <ReportingTemplateDelete
        isOpen={deleteModalOpen}
        onClose={() => setUIState(prevState => ({ ...prevState, deleteModalOpen: false }))}
        onComplete={onDeleteComplete}
        template={data}
      />}
      {deleteLayoutModalOpen && !!selectedLayout && <DeleteReportingTemplateLayout
        isOpen={deleteLayoutModalOpen}
        onClose={() => setUIState(prevState => ({ ...prevState, deleteLayoutModalOpen: false }))}
        onComplete={onDeleteLayoutComplete}
        reportingTemplateId={reportingTemplateId}
        layout={selectedLayout}
      />}

      <Container className='template-edit' noOverflow>
        {error && <Banner type='error' active={!!error} message={error?.message} />}
        {apiError != null && <Banner type='error' active={!!apiError} message={apiError} />}
        <InlineGroup spread verticalCenter>
          <h2>{data?.name ?? 'Template Details'}</h2>
          {!!data &&
          <InlineGroup spread spaceLeft={2}>
            <GenericPreview reportingTemplateId={data.id} />
            <AutoConfigure reportingTemplateId={data.id} onFinish={() => refresh()} />
            <AddReportingTemplateLayout template={data} onFinish={() => refresh()}/>
            <CloneReportingTemplate template={data} type='button' />
            <IconButton
              data-test={`edit-template-${reportingTemplateId}`}
              icon='edit'
              text='Edit Template'
              linkTo={`/reporting-templates/${reportingTemplateId}/edit`}
            />
            <IconButton
              danger
              data-test={`delete-template-${data.id}`}
              icon='trash'
              text='Delete'
              onClick={() => setUIState(prevState => ({ ...prevState, deleteModalOpen: true }))}
            />
          </InlineGroup>}
        </InlineGroup>
        {inProgress && !data && <LoadingSpinner />}
        {!!data &&
          <InlineGroup ref={containerRef} className='mb-4' fullHeight spaceLeft={2}>
            {containerHeight > 0 &&
              <>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId={reportingTemplateId}>{
                    (provided) => <VerticalGroup
                      spaceBetweenElements={2}
                      style={{ overflowY: 'auto', maxHeight: containerHeight, width: THUMBNAIL_COLUMN_WIDTH }}
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {layouts.map((l, index) => (
                        <Draggable draggableId={l.id} index={index}>{
                          (provided) => {
                            return <Clickable
                              // @ts-expect-error
                              ref={provided.innerRef}
                              key={l.id}
                              style={{ ...provided.draggableProps.style }}
                              onClick={() => setSelectedLayout(l)}
                              {...provided.draggableProps}
                            >
                              <ReportingPagePreviewTile
                                data={{
                                  ...fakePreviewData,
                                  layouts: [{...l, enabled: true}]
                                }}
                                disabled={apiUpdating}
                                layout={l}
                                onLayoutUpdate={onLayoutUpdate}
                                dragHandleProps={provided.dragHandleProps}
                              />
                            </Clickable>
                          }
                        }
                        </Draggable>
                      ))}
                    </VerticalGroup>
                  }</Droppable>
                </DragDropContext>
                <div style={{ flex: 1, width: '100%', height: containerHeight }}>
                  {selectedLayout && <InlineGroup spaceLeft={2} style={{flex: 1, height: containerHeight, width: '100%'}} spread center>
                    {previewHeight && previewWidth &&
                      <ContentStretcher
                        style={{
                          background: '#e1e1e1',
                          width: previewWidth,
                          height: previewHeight,
                          minHeight: previewHeight
                        }}
                        contentWidth={RENDERED_DIMENSIONS.width}
                        contentHeight={RENDERED_DIMENSIONS.height}
                      >
                        <div style={{ width: RENDERED_DIMENSIONS.width, height: RENDERED_DIMENSIONS.height, minHeight: RENDERED_DIMENSIONS.height, position: 'absolute' }}>
                          <RoundResultsPage
                            key={selectedLayout.id}
                            data={{
                              ...fakePreviewData,
                              layouts: [{...selectedLayout, enabled: true }]
                            }}
                            roundNumber={3}
                            onlyThesePages={[selectedLayout?.type]}
                            hidePagination
                            hideTeamPositions
                            allowCustomTemplateEdits
                            onCustomTemplateEdit={onCustomTemplateEdit}
                          />
                        </div>
                      </ContentStretcher>

                    }
                    <VerticalGroup style={{ width: SIDEBAR_WIDTH - 40 }} spaceBetweenElements={2}>
                      <Text bold>Slide Controls</Text>
                      <VerticalGroup spaceBetweenElements={2}>
                        {selectedLayout.type === 'custom_image' && selectedLayout.config.type === 'custom_image' && selectedLayout.config.imageUrl != null &&
                          <Button
                            disabled={apiUpdating}
                            onClick={() => onCustomTemplateEdit({
                              config: {
                                ...selectedLayout.config,
                                imageId: null,
                                imageUrl: null
                              }
                            })}
                          >Remove Image</Button>}

                        <Button disabled={apiUpdating} onClick={() => onLayoutUpdate(selectedLayout.id, { enabled: !selectedLayout.enabled })}>{selectedLayout.enabled ? 'Disable' : 'Enable'}</Button>
                        {['custom_image', 'custom_text'].includes(selectedLayout.type) && <Button
                          danger
                          data-test={`delete-template-${data.id}`}
                          onClick={() => setUIState(prevState => ({ ...prevState, deleteLayoutModalOpen: true }))}
                        >Delete</Button>}
                      </VerticalGroup>
                      {['custom_image', 'custom_text'].includes(selectedLayout.type) && <EditRoundRestrictions reportingTemplateId={reportingTemplateId} layout={selectedLayout} onFinish={() => refresh()} />}
                    </VerticalGroup>
                  </InlineGroup>}
                </div>
              </>}
          </InlineGroup>
        }
      </Container>
    </>
  )
}

export default ReportingTemplateDetails
